<script lang="ts" setup>
import FilterService, { parseContent } from '../../../runtime/services/FilterService';
import CreateModal from '../modals/CreateModal.vue';
import EditModal from '../modals/EditModal.vue';

const canManage = defineModel<boolean>('manage');
const props = withDefaults(defineProps<{
    advanced?: boolean;
    border?: boolean;
}>(), {
    advanced: true,
    border: true
});
const emit = defineEmits<{
    (e: 'change'): void;
}>();

const { $alert } = useAlerts();
const { $t } = useLocalization();

const filter = inject<FormType<any>>('filter');
const components = inject<Ref<any[]>>('components');
const dirty = inject<Ref<string[]>>('dirty');
const visible = inject<Ref<string[]>>('visible');
const reset = inject<(name?: string) => void>('reset');
const apply = inject<() => void>('apply');

const currentFilter = computed(() => getFilterState());
const activeFilter = computed(() => currentFilter.value.activeTemplate.id > 0);
const isDirty = computed(() => dirty.value.length > 0);

const createModal = ref(false);
const editModal = ref(false);

function onCreate()
{
    createModal.value = true;
}

function onEdit()
{
    editModal.value = true;
}

async function onUpdate()
{
    try
    {
        const template = currentFilter.value.activeTemplate;

        template.contentJson = JSON.stringify({
            visible: visible.value,
            form: filter.data()
        });

        await FilterService.update(template.id, template);

        setFilterState({
            activeTemplate: template,
            visible: visible.value,
            form: filter
        });
        apply();

        $alert.success($t('[[[Filtr został zapisany]]]'));

        dirty.value.clear();
        onSave();
    }
    catch
    {
        $alert.danger($t('[[[Nie udało się zapisać filtra]]]'));
    }
}

function onReset()
{
    reset();
    currentFilter.value.activeTemplate = { id: 0 };
    visible.value = components.value.where(p => p.visible).select(p => p.name).toArray();
    apply();
}

function onRestore()
{
    reset();

    const template = currentFilter.value.activeTemplate;
    const content = parseContent(template.contentJson);

    filter.withData(content.form);
    visible.value = content.visible;

    setFilterState({
        activeTemplate: template,
        visible: content.visible,
        form: filter
    });

    apply();
}

function onSave()
{
    emit('change');
}
</script>

<template>
    <template v-if="activeFilter || isDirty">
        <div v-if="props.border" class="border-start ms-1 me-2" />
        <template v-if="!activeFilter">
            <IdeoButton v-if="props.advanced" variant="success" :disabled="!isDirty" @click="onCreate">
                {{ $t('[[[Zapisz filtr]]]') }}
            </IdeoButton>
            <IdeoButton v-if="isDirty" variant="none" @click="onReset">
                {{ $t('[[[Resetuj]]]') }}
            </IdeoButton>
        </template>
        <template v-else>
            <IdeoDropdown v-if="props.advanced" variant="success" :text="$t('[[[Zapisz filtr]]]')" no-caret>
                <IdeoDropdownItemButton :disabled="!isDirty" @click="onUpdate">
                    {{ $t('[[[Zapisz zmiany]]]') }}
                </IdeoDropdownItemButton>
                <IdeoDropdownItemButton @click="onEdit">
                    {{ $t('[[[Edytuj filtr]]]') }}
                </IdeoDropdownItemButton>
                <IdeoDropdownItemButton @click="onCreate">
                    {{ $t('[[[Utwórz nowy]]]') }}
                </IdeoDropdownItemButton>
            </IdeoDropdown>
            <IdeoButton v-if="props.advanced && isDirty" variant="none" @click="onRestore">
                {{ $t('[[[Przywróć filtr]]]') }}
            </IdeoButton>
            <IdeoButton v-else variant="none" @click="onReset">
                {{ $t('[[[Resetuj]]]') }}
            </IdeoButton>
        </template>
        <CreateModal v-model="createModal" :can-manage="canManage" @save="onSave" />
        <EditModal v-model="editModal" :can-manage="canManage" @save="onSave" />
    </template>
</template>
